<script lang="ts">
  import DifficultyDiamonds from '../../DifficultyDiamonds.svelte';
  import type { MyFitnessStores } from '../stores';
  import { Matchstick, TrainingStatusGradient, workoutNameMap } from '../../Common';
  import { endOfDay, format, isToday, isTomorrow, parse, startOfDay } from 'date-fns';
  import SimpleModal from './SimpleModal.svelte';
  import type { SPFocus } from '../../Util/api';
  import TrainingXssDetailsCircle from './TrainingXSSDetailsCircle.svelte';
  import { type XSSDetails, convertSeconds, isActivityCompleted, noActivity as generateNoActivity, StrToProgramType, switchMyFitnessTab } from '@/Util/utils';
  import ForecastEventsBar from './../ForecastEventsBar.svelte';
  import { Activity } from '@/Common/ActivityFunctions';
  import { ProgramType, programTypeIcon, shortProgramTypeTitle } from '@/Common/constants';
  import { cn } from '@/lib/utils';
  import { ActivityTag } from '@/Common/ActivityTag';
  import TagWrapper from '@/Common/TagWrapper.svelte';

  export let index = 0;
  export let myFitness: MyFitnessStores;
  export let activity;
  export let isStale: boolean = false;
  export let isModal = false;
  export let training_status_array: any | null = null; // expects an array of training statuses for this day
  export let preparedGradientString: string | null = null; // Instead of the array, you could directly use a gradient string instead
  export let activities: any[] = [];
  export let forceGrayCircle: boolean = false;
  export let conflictDay: boolean = false;
  export let isCalendar: boolean = false;

  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const { selectedTrainingPlan, trainingPlan } = myFitness;

  $: programType = StrToProgramType($trainingPlan?.program_type, $trainingPlan?.selected_mode)

  let forecastedXSSDetails: XSSDetails | null;
  let noActivity: boolean = true;
  let isPlaceholder: boolean = false;
  let isForecast: boolean = false;
  let isCompletedActivity: boolean = false;
  let isDateToday: boolean = false;
  let isTodayCompletionInProgress: boolean = false;
  let date: Date;
  let day: String;
  let showActivityList = false;
  let thisXSSDetails: XSSDetails | null;
  let exerciseType: string | null = null;
  let mainXSSDetailsTableTitle: string = '';
  let isCurrentProfile = activity.isCurrentProfile;

  function updateTags(activity) {
    const activityTag = ActivityTag("default")
    return [
      activityTag.exerciseType({exerciseType}),
      activityTag.pinned(activity) ?? activityTag.forecast(activity),
      ...activityTag.list(activity.tags)
    ].filter(tag => tag)
  }

    // NOTE: Missing activity/activities should have been filled in ForecastTraining.svelte
    $: if (activity && activities) {

        let parsedDate = activity.start_date_local
        if (activity.start_date_local?.length > 19) {
            parsedDate = activity.start_date_local.substring(0, activity.start_date_local.length - 8); // remove the .000000Z at the end
        }
        date = new Date(parsedDate);
        isDateToday = isToday(date)
        noActivity = activity.state && (activity.state.findIndex(v=>['none', 'rest', 'recovery'].includes(v)) >= 0)
        isCompletedActivity = isActivityCompleted(activity)
        isPlaceholder = !!(activity.state?.includes('placeholder'))
        isForecast = !!(activity.state?.includes('forecast') && !activity.state?.includes('completed'))
        forecastedXSSDetails = activity.placeholder_xss_details ? activity.placeholder_xss_details : null
        exerciseType = activity.exerciseType ?? (activity.session_instance_id ? "Session" : activity.workout_id ? 'Workout' : 'Activity')
        isTodayCompletionInProgress = isDateToday && activities && activities.map(a=>a.state).flat().includes('completed')

    if (activities.length == 1 || (isForecast && date > endOfDay(new Date()))) {
      thisXSSDetails = {
        xss: activity.xss ?? 0,
        xlss: activity.xlss ?? 0,
        xhss: activity.xhss ?? 0,
        xpss: activity.xpss ?? 0,
      };
    } else {
      thisXSSDetails = {
        xss: 0,
        xlss: 0,
        xhss: 0,
        xpss: 0,
      };
      activities.forEach((a) => {
        if (a.placeholder_xss_details) forecastedXSSDetails = a.placeholder_xss_details;
        const isThisCompleted = isActivityCompleted(a);
        if (isTodayCompletionInProgress && !isThisCompleted) return;
        thisXSSDetails.xss = thisXSSDetails.xss + (a.xss ?? 0);
        thisXSSDetails.xlss = thisXSSDetails.xlss + (a.xlss ?? 0);
        thisXSSDetails.xhss = thisXSSDetails.xhss + (a.xhss ?? 0);
        thisXSSDetails.xpss = thisXSSDetails.xpss + (a.xpss ?? 0);
      });
    }

    if (isCompletedActivity || isTodayCompletionInProgress) mainXSSDetailsTableTitle = 'Completed';
    else if (isPlaceholder) mainXSSDetailsTableTitle = 'Forecasted';
    else mainXSSDetailsTableTitle = 'Scheduled';
  }

  $: if (date) {
    day = isDateToday ? 'Today' : isTomorrow(date) ? 'Tomorrow' : weekday[date.getDay()];
  }

  const getDescription = (date: Date): string => {
    if (activity?.state?.includes('recovery')) {
      return `Recovery Day`;
    } else if (activity?.state?.includes('rest') || activity.xss === 0) {
      return `Rest Day`;
    } else if (activity?.state?.includes('none')) {
      return `No forecasted training`;
    }

    const desc_time = new Date(date);
    const hours = desc_time.getHours();
    const timeString = format(desc_time, 'hh:mm aa');
    let prefix: string = '';
    // if (isActivityCompleted(activity)) {
    //     prefix = ''//``
    // }
    // else if (hours >= 12 && hours < 19) {
    //     prefix = 'Afternoon '
    // } else if (hours >= 0 && hours <= 11) {
    //     prefix = 'Morning '
    // } else {
    //     prefix = 'Evening '
    // }

    // return `${prefix}${timeString}`; // unnecessary prefix?
    return `${timeString}`;
  };

  let root: HTMLElement;
  let isCalendarEventLoading: boolean = false;
  const dispatchEditEvent = (exercise) => {
    let event = new CustomEvent('editEvent', {
      bubbles: true,
      detail: {
        date,
        exercise,
        setIsLoading: (v: boolean) => {
          isCalendarEventLoading = v;
        },
      },
    });
    selectedTrainingPlan.set(exercise);
    root.dispatchEvent(event);
  };
  export let onClickCallback: Function = (activity: any) => {
    if (activity.state?.includes('rest'))  return;
    return dispatchEditEvent(activity);
  }; // Default callback used by calendar mobile view

  let activityHrefUrl: string | null = null;
  $: _exerciseUrl = activity.workout_id ? '/workout/' + activity.workout_id : '/activities/' + (activity.related_activity_id ?? activity.path);
  $: activityHrefUrl = !isPlaceholder && !(activities && activities.length > 1) ? _exerciseUrl : null;

  const openActivityList = (e) => {
    if (isModal || !activities || activities.length <= 1) return;
    e.preventDefault();
    showActivityList = !showActivityList;
  };

  const matchstickWidth = 8;

  let focus: string | null = null;
  let focusDesc: string | null = null;
  $: {
    // Activities sometimes have "focus", sometimes "fa" field.
    focus = activity.focus ?? activity.fa;
    focusDesc = workoutNameMap.get(focus) ?? focus;
  }

</script>

<div style={"min-height:fit-content;"+ (isCalendar?"box-shadow: 0 0 2px 2px #aaa":"")}>
    <div bind:this={root} class:isCalendarEventLoading class="border-solid border-2 max-w-full outer-grid relative training-card" style={!isModal && $selectedTrainingPlan?.start_date_local == activity?.start_date_local ? '' : 'border-color: transparent'}>
        {#if isCalendarEventLoading}
            <i class="fa-regular fa-spinner-third fa-2xl fa-spin absoluteSpinner"></i>
        {/if}
        <div class = "border-2 max-w-full inner-grid relative training-card">
            <Matchstick {noActivity} {conflictDay} xss={activity.xss} xlss={activity.xlss} xhss={activity.xhss} xpss={activity.xpss} width={matchstickWidth} />
            <div 
                class="card-container header clickable"
                on:keydown={() => {}} role="menu" tabindex=0
                on:click={() => {
                    if (activity.state?.includes('event_A')) {
                      switchMyFitnessTab("program", root)
                    } else {
                      onClickCallback(activity)
                    }				
                }}
            >

                <div class="card-topleft">
                    <button title="Choose Training">
                        {#if activity}
                            <div class="title">
                                {#if isCompletedActivity && isModal && activity?.type}
                                    {@const typeLower = activity.type.toLowerCase()}
                                    {#if typeLower == 'ride' || typeLower == 'cycling'}
                                        <i class="fa-light fa-bicycle"></i>
                                    {:else if typeLower == 'run' || typeLower == 'running' || typeLower == 'hike'}
                                        <i class="fa-light fa-walking"></i>
                                    {:else if typeLower == 'swim' || typeLower == 'swimmer'}
                                        <i class="fa-light fa-swimmer"></i>
                                    {/if}
                                    {activity.type}
                                {:else if isCompletedActivity && activity?.start_date && isToday(new Date(activity.start_date))}
                                    Today's Completed Training
                                {:else}
                                    {day + "'s Training "}
                                    <i class="fa-light fa-calendar-pen"></i>
                                {/if}
                                {#if date}
                                    <div class="description">
                                        {#if activity?.state?.includes('event_A')}
                                            <i class={cn("fa-light text-black ml-1", programTypeIcon[programType])}/>
                                            {shortProgramTypeTitle[programType]} Date
                                        {:else}
                                            {getDescription(date)}
                                        {/if}
                                    </div>
                                {/if}
                            </div>
                        {:else}
                            <div class="title">No Activity</div>
                        {/if}
                    </button>
                    {#if !noActivity}
                        <div class="difficulty-wrapper mt-2">
                            <TrainingXssDetailsCircle {thisXSSDetails} {forecastedXSSDetails} {isPlaceholder} isMatched={!!activity.matched_activity} {focus} {mainXSSDetailsTableTitle} displayRemainingXSS={isTodayCompletionInProgress} specRating={activity.spr} skipDeviationClassCalculation={noActivity || isPlaceholder || forceGrayCircle} />
                        </div>
                    {:else if activity.state?.includes('none') !== true}
                        <div class="difficulty-wrapper">
                            <div class="difficulty noTraining">
                            {#if activity.state?.includes('event_A')}
                                <img class="max-h-[100px] opacity-40 py-[8px] mt-2" src="assets/images/trophy.svg" aria-hidden="true" alt="Goal day graphic" />
                            {:else}
                                <img class="max-h-[100px]" src="assets/images/Rest_Day_Graphic.svg" aria-hidden="true" alt="Rest/Recovery day graphic" />
                            {/if}
                            </div>
                        </div>
                    {/if}
                </div>

                <div class="body-info">
                    {#if !noActivity && activity.xss !== 0}
                        <a href={activityHrefUrl} on:click={openActivityList} class:clickable={(!isPlaceholder || activities.length > 1) && !isModal && activities.length > 1} class:modalIsOpen={showActivityList} class:noTraining={(isPlaceholder && activities.length == 1) || noActivity || activity.xss == 0} target="activity" rel="noopener noreferrer">
                            <div class="bold workout-name">
                                {#if !isForecast && activities && activities.length > 1}
                                    {activities.length} activities
                                {:else}
                                    {activity.name}
                                    {#if (!activity.state?.includes('event_A') || isCompletedActivity || programType === ProgramType.Race)}
                                        <i class="fa fa-external-link" aria-hidden="true" style="color:black !important;"></i>
                                    {/if}
                                {/if}
                            </div>
                        </a>
                        <div class="bold">
                            {activity.spr + ' ' + focusDesc}
                        </div>
                        {#if !activity.state?.includes('event_A')}
                            <div class="bold">
                                <i class="fa-thin fa-clock"></i>
                                {convertSeconds(activity.duration ?? activity.adu)}
                            </div>
                        {/if}
                        {#if !isPlaceholder}
                            {#if activity.difficulty}
                                <div>
                                    <DifficultyDiamonds style="white-space: nowrap; " difficulty={activity.difficulty} />
                                </div>
                            {/if}
                            <TagWrapper class="mt-2" tags={updateTags(activity)} />
                        {/if}
                    {:else}
                        <div class="bold workout-name">
                            {#if activity?.state?.includes('event_A')}
                                {''}
                            {:else if activity?.state?.includes('rest') || activity.xss === 0}
                                Training Optional
                            {:else if activity?.state?.includes('recovery')}
                                No Training / Active Recovery
                            {/if}
                        </div>
                        <div >
                            <!-- class="workout-name" -->
                            {#if activity?.state?.includes('event_A')}
                                {''}
                            {:else if activity?.state?.includes('recovery')}
                                Focus on recovery and reducing activity
                            {:else if activity?.state?.includes('rest') || activity.xss === 0}
                                Focus on other activities
                            {:else}
                                Schedule an activity to see it here
                            {/if}
                        </div>
                    {/if}
                    {#if isForecast && !isModal && activities && activities.length > 1}
                        <div>
                            + {activities.length - 1} more
                        </div>
                    {/if}
                    {#if !isModal && activities && activities.length > 1}
                        <SimpleModal modalBottom={false} modalRight={false} bind:showModal={showActivityList}>
                            {#each activities as activity}
                                {#if !activity.state?.includes('placeholder')}
                                    <a href={'/activities/' + activity.path} class="bold clickable text-black" target="activity" rel="noopener noreferrer">
                                    {activity.name}
                                    </a>
                                {/if}
                            {/each}
                        </SimpleModal>
                    {/if}
                </div>
            </div>
        </div>
        {#if training_status_array || preparedGradientString}
            <div class="gradient-wrapper">
                <TrainingStatusGradient {training_status_array} {preparedGradientString} height={matchstickWidth} />
            </div>
        {/if}
    </div>
  
    {#if isCalendar}
        <div class="event-bar-wrapper"
            on:deleteActivity={async (e) =>{
                // isCardLoading = true;
                let exercise = e.detail.exercise;
                await Activity.clearFromCalendar(exercise);
                // isCardLoading = false
            }}
            on:onTogglePinClick={(e)=>{
                // isPinned = !isPinned;
                let exercise = e.detail.exercise;
                Activity.togglePin(exercise);
            }}
            
            on:starActivity={(e) =>{
                // isStarred = !isStarred;
                let exercise = e.detail.exercise;
                Activity.toggleFavorite(exercise);
            }}
            on:leaveSession={(e) =>{
                let exercise = e.detail.exercise;
                Activity.leaveSession(exercise);
            }}
            on:flagActivity={(e) =>{
                // isFlagged = !isFlagged;
                let exercise = e.detail.exercise;
                Activity.toggleFlag(exercise);
            }}
        >
            <ForecastEventsBar {myFitness} exercise={activity} {date} isCardLoading={false} otherProfile={!isCurrentProfile} isMobile={true}></ForecastEventsBar>
        </div>
    {/if}
</div>

<style lang="scss">
  .training-card {
    display:flex;
    flex-direction: column;
    background-color: #f2f2f2;
    min-height: fit-content;
  }
  .isCalendarEventLoading {
    pointer-events: none;
    filter: brightness(0.75);
  }
  .absoluteSpinner {
    position: absolute;
    top: 50%;
    left: calc(50% - 16px);
    transform: translate(-50%, -50%);
    color: #016e79;
    z-index: 2;
  }
  .outer-grid {
    // width: 100%;
    height: 100%;
    min-height: fit-content;
    // display: grid;
    display: flex;
    flex-flow: column ;
    // grid-template-columns: 8px auto;
    // grid-template-rows: auto 8px;
  }
  .inner-grid {
    // width: 100%;
    height: 100%;
    min-height: fit-content;
    // display: grid;
    display: flex;
    flex-flow: row nowrap;
    // grid-template-columns: 8px auto;
    // grid-template-rows: auto 8px;
  }
  .gradient-wrapper {
    width: 100%;
    // grid-area: auto / 1 / auto / end;
  }
  @mixin grid-row-format {
    .card-container {
      min-width: 0;
      flex-direction: row !important;
      .card-topleft {
        max-width: 100% !important;
        // flex-grow: 0 !important;
        .difficulty-wrapper {
          padding: 0 !important;
        }
      }
      .body-info {
        width: auto !important;
        margin: 0 !important;
        padding: 10px 12px !important;
        &.noTraining {
          text-align: center;
          &:hover {
            cursor: default;
          }
        }
      }
    }
  }
  @mixin tall-format {
    .card-container {
      min-width: 0;
      flex-direction: column !important;
      .card-topleft {
        max-width: 100% !important;
        flex-grow: 0 !important;
        .difficulty-wrapper {
          padding: 0 !important;
        }
      }
      .body-info {
        width: auto !important;
        margin: 0 !important;
        padding: 10px 12px !important;
        &.noTraining {
          text-align: center;
          &:hover {
            cursor: default;
          }
        }
      }
    }
  }
  // breakpoints should be the same as the ones in TrainingUpcomingCards.svelte
  @supports (contain: inline-size) {
    // container is in ForecastTraining.svelte
    @container cards-container (min-width: 600px) {
      @include grid-row-format;
    //   @container cards-container (max-width: 900px) {
    //     @include tall-format;
    //   }
    }
  }
  @supports not (contain: inline-size) {
    @media (min-width: 1860px), (max-width: 993px) and (min-width: 790px) {
      @include grid-row-format;
      @media (max-width: 2060px) {
        @include tall-format;
      }
    }
  }
  .bold {
    font-weight: bold;
    color: black !important;
  }
  .card-container {
    // position: relative;
    display: flex !important;
    flex-direction: row;
    flex-wrap: wrap;
    // justify-content: flex-start;
    // width: 100%;
    // width:inherit;
    // max-width: 100%;
    min-width: 175px;
    height: 100%;
    min-height: fit-content;
    // background-color: #F2F2F2;
    // grid-area: auto / 2 / auto / end;
    width: 100%;
    overflow: hidden;
    .body-info {
      color: black !important;
      // position: relative;
      // max-height: 100%;
      min-height: fit-content;
      // margin: 0 0 auto max(0px, calc(33% - 120px));
      padding: 4px 15px 4px 5px;
      // box-sizing: content-box
      min-width: 170px;
      flex: 9 1 170px;
      .relativeWrapper {
        position: relative;
        padding: 0;
      }
    }
    .card-topleft {
      display: flex;
      flex-direction: column;
    //   flex-grow:1;
      justify-content: flex-start;
    //   max-width: 155px;
      min-width: 155px;
      min-height: max-content;
      // flex-grow: 1;
      // max-height: 100%;
    //   flex-basis: 170px;
      flex: 1 1 170px;
      .difficulty-wrapper {
        padding: 10px 0 10px 10px;
        margin: 0 auto;
      }
    }
  }
  .workout-name {
    // display: block;
    white-space: nowrap;
    max-height: 2.7rem;
    margin-bottom: 5px;
    text-overflow: ellipsis;
    overflow: hidden;
    color: black !important;
    &:hover {
      color: black !important;
    }
    > .i:hover {
      color: black !important;
    }
    margin-top: 5px;
  }
  .text-black {
    color: black;
  }
  .header {
    color: black;
    margin: 5px;
    padding: 5px;
    // white-space: nowrap;
    .title {
      // display:flex;
      // flex-wrap: wrap;
      // gap:3px;
      font-weight: bold;
      // width: 100%;
    }
    .description {
      // width: 100%;
      color: black !important;
      font-family: Roboto;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      letter-spacing: 0.14px;
      opacity: 0.8;
    }
  }
  a.clickable,
  .clickable {
    padding: 5px;
    border-radius: 5px;
    transition: color 0.05s;
    display: block;
    &:hover,
    &.modalIsOpen {
      cursor: pointer;
      transition: color 0.15s;
    }
  }
  .XSSDetails {
    top: 5%;
    left: 100%;
  }

  button {
    all: unset;
  }
</style>
